import { z } from 'zod';
import { uuidSchema } from './common.js';
/**
 * CAMPAGNES MARKETING — l'envoi d'un message à une partie de la clientèle.
 *
 * ⭐ Le vocabulaire tient en trois mots : une CAMPAGNE (ce qu'on veut dire),
 * un SEGMENT (à qui), un DESTINATAIRE (une ligne partie, ou non, avec sa
 * raison). Tout le module en découle.
 *
 * ⚠️ Le canal est WhatsApp, et WhatsApp n'est pas un SMS : un message
 * publicitaire y passe par un MODÈLE approuvé par Meta, hors de quoi il n'est
 * remis que dans les 24 h suivant un message du client. Cette règle appartient
 * à l'opérateur, pas à ce code : elle est écrite ici pour que personne ne
 * découvre au premier envoi réel pourquoi 300 messages sont revenus en échec.
 */
// ------------------------------------------------------------------ statuts
export const STATUTS_CAMPAGNE = [
    'draft',
    'scheduled',
    'running',
    'done',
    'paused',
    'failed',
    'cancelled',
];
export const LIBELLES_STATUT_CAMPAGNE = {
    draft: 'Brouillon',
    scheduled: 'Programmée',
    running: 'En cours',
    done: 'Terminée',
    paused: 'Suspendue',
    failed: 'Échouée',
    cancelled: 'Annulée',
};
/** Couleur de la pastille. Le vert est réservé à ce qui est allé au bout. */
export const COULEURS_STATUT_CAMPAGNE = {
    draft: 'default',
    scheduled: 'info',
    running: 'warning',
    done: 'success',
    paused: 'warning',
    failed: 'error',
    cancelled: 'default',
};
/**
 * Ce qu'on a le droit de faire selon l'état — écrit UNE fois, ici.
 *
 * ⚠️ Le serveur s'en sert pour refuser, l'écran pour griser. Deux listes
 * séparées auraient fini par diverger, et un bouton actif qui déclenche un 409
 * est pire qu'un bouton absent.
 */
export const TRANSITIONS_CAMPAGNE = {
    draft: ['scheduled', 'running', 'cancelled'],
    scheduled: ['running', 'draft', 'paused', 'cancelled'],
    running: ['paused', 'done', 'failed', 'cancelled'],
    paused: ['running', 'cancelled'],
    done: [],
    failed: ['scheduled', 'draft'],
    cancelled: [],
};
export function transitionPossible(de, vers) {
    return TRANSITIONS_CAMPAGNE[de].includes(vers);
}
/** Une campagne peut-elle encore être modifiée ? Une fois partie, non. */
export function campagneModifiable(statut) {
    return statut === 'draft' || statut === 'scheduled' || statut === 'failed';
}
// ----------------------------------------------------------------- segments
export const TYPES_SEGMENT = ['all', 'active', 'inactive', 'vip', 'custom'];
export const LIBELLES_SEGMENT = {
    all: 'Tous les clients',
    active: 'Clients actifs',
    inactive: 'Clients inactifs',
    vip: 'Clients VIP',
    custom: 'Critères personnalisés',
};
export const DESCRIPTIONS_SEGMENT = {
    all: 'Toute la clientèle disposant d’un numéro de téléphone.',
    active: 'Venus au moins une fois sur la période choisie.',
    inactive: 'Sans passage depuis la durée choisie — à faire revenir.',
    vip: 'Portant l’étiquette VIP, ou ayant dépassé un montant d’achats.',
    custom: 'Étiquettes, ville, montant dépensé, date de dernière visite.',
};
const jourSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Date attendue en AAAA-MM-JJ');
export const segmentSchema = z.discriminatedUnion('type', [
    z.object({ type: z.literal('all') }),
    /** « Actif » n'a pas de sens absolu : un salon compte en semaines, un
     *  négociant en trimestres. La fenêtre est donc un choix, pas une constante. */
    z.object({
        type: z.literal('active'),
        joursActivite: z.coerce.number().int().min(1).max(3650).default(90),
    }),
    z.object({
        type: z.literal('inactive'),
        joursInactivite: z.coerce.number().int().min(1).max(3650).default(180),
    }),
    /** ⭐ Deux façons d'être VIP, réunies par un OU : l'étiquette posée à la main
     *  et le montant dépensé. Exiger les deux aurait vidé le segment. */
    z.object({
        type: z.literal('vip'),
        etiquette: z.string().trim().max(40).default('VIP'),
        seuilDepense: z.coerce.number().int().min(0).optional(),
    }),
    z.object({
        type: z.literal('custom'),
        etiquettes: z.array(z.string().trim().max(40)).max(10).optional(),
        ville: z.string().trim().max(80).optional(),
        depenseMin: z.coerce.number().int().min(0).optional(),
        depenseMax: z.coerce.number().int().min(0).optional(),
        /** Dernière visite AVANT cette date — pour rattraper ceux qu'on perd. */
        derniereVisiteAvant: jourSchema.optional(),
        /** Dernière visite APRÈS cette date — pour récompenser les fidèles. */
        derniereVisiteApres: jourSchema.optional(),
        /** Fiches créées après cette date — les nouveaux venus. */
        creeApres: jourSchema.optional(),
    }),
]);
/** Résumé d'un segment en une ligne, pour la liste et le rapport. */
export function decrireSegment(s) {
    switch (s.type) {
        case 'all':
            return 'Tous les clients';
        case 'active':
            return `Clients venus depuis moins de ${s.joursActivite} jours`;
        case 'inactive':
            return `Clients sans passage depuis ${s.joursInactivite} jours`;
        case 'vip':
            return s.seuilDepense
                ? `Clients « ${s.etiquette} » ou ayant dépensé plus de ${s.seuilDepense}`
                : `Clients étiquetés « ${s.etiquette} »`;
        case 'custom': {
            const parts = [];
            if (s.etiquettes?.length)
                parts.push(`étiquettes ${s.etiquettes.join(', ')}`);
            if (s.ville)
                parts.push(`ville de ${s.ville}`);
            if (s.depenseMin !== undefined)
                parts.push(`dépense ≥ ${s.depenseMin}`);
            if (s.depenseMax !== undefined)
                parts.push(`dépense ≤ ${s.depenseMax}`);
            if (s.derniereVisiteAvant)
                parts.push(`vus avant le ${s.derniereVisiteAvant}`);
            if (s.derniereVisiteApres)
                parts.push(`vus après le ${s.derniereVisiteApres}`);
            if (s.creeApres)
                parts.push(`inscrits après le ${s.creeApres}`);
            return parts.length ? `Critères : ${parts.join(', ')}` : 'Critères personnalisés';
        }
    }
}
// ------------------------------------------------------------------ options
export const optionsCampagneSchema = z.object({
    /**
     * Messages par minute. ⚠️ Ce n'est pas un réglage de confort : au-delà d'une
     * certaine cadence, un numéro neuf est traité comme un émetteur de masse et
     * se fait limiter par l'opérateur. Le défaut est prudent exprès.
     */
    cadenceParMinute: z.coerce.number().int().min(1).max(120).default(20),
    /** Fenêtre d'envoi, en heures locales : personne ne veut d'une promotion à
     *  3 h du matin, et un message reçu la nuit se lit comme du démarchage. */
    heureDebut: z.coerce.number().int().min(0).max(23).default(9),
    heureFin: z.coerce.number().int().min(1).max(24).default(20),
    /** Au-delà de ce taux d'échec, l'agent suspend au lieu de s'entêter. */
    seuilEchecPct: z.coerce.number().int().min(1).max(100).default(25),
    /** Éviter les doublons de numéro entre deux fiches du même foyer. */
    eviterDoublons: z.boolean().default(true),
});
export const OPTIONS_PAR_DEFAUT = optionsCampagneSchema.parse({});
/**
 * L'heure qu'il est DANS LA BOUTIQUE, pas sur le serveur.
 *
 * ⚠️ `formatToParts`, JAMAIS `format()`. En français, `format()` rend « 19 h »
 * — avec l'unité — et `Number('19 h')` vaut NaN. Toute comparaison avec NaN
 * étant fausse, la fenêtre d'envoi se refermait à toute heure et AUCUNE
 * campagne ne partait, sans le moindre message d'erreur. Le défaut est
 * invisible en anglais, où `format()` rend « 19 » : c'est ce qui l'a laissé
 * passer.
 *
 * ⚠️ `hourCycle: 'h23'` et non `hour12: false` : ce dernier laisse le choix
 * entre h23 et h24 selon l'implémentation, et minuit y devient « 24 », qui
 * tombe hors de toute fenêtre finissant à 24 h.
 */
export function heureLocale(fuseau, quand = new Date()) {
    try {
        const parties = new Intl.DateTimeFormat('en-GB', {
            timeZone: fuseau,
            hour: '2-digit',
            hourCycle: 'h23',
        }).formatToParts(quand);
        const h = Number(parties.find((p) => p.type === 'hour')?.value);
        // Ceinture ET bretelles : un NaN qui passerait ici refermerait la fenêtre
        // pour toujours, en silence. Mieux vaut l'heure du serveur, quitte à
        // décaler d'une heure ou deux.
        return Number.isFinite(h) ? h : quand.getHours();
    }
    catch {
        // Fuseau inconnu : on n'envoie pas « jamais », on envoie décalé.
        return quand.getHours();
    }
}
/** L'envoi est-il permis à cette heure-ci, dans cette boutique ? */
export function dansLaFenetre(options, fuseau, quand = new Date()) {
    const h = heureLocale(fuseau, quand);
    return h >= options.heureDebut && h < options.heureFin;
}
// --------------------------------------------------------------- la campagne
/** Marques remplacées à l'envoi, une par destinataire. */
export const TOKENS_MESSAGE = ['{{prenom}}', '{{nom}}', '{{boutique}}'];
export function appliquerTokens(message, vars) {
    return (message
        .split('{{prenom}}')
        .join(vars.prenom?.trim() ?? '')
        .split('{{nom}}')
        .join(vars.nom?.trim() ?? '')
        .split('{{boutique}}')
        .join(vars.boutique?.trim() ?? '')
        // Un prénom absent laisse « Bonjour  , » — deux espaces et une virgule
        // orpheline. On recolle plutôt que d'envoyer une salutation bancale.
        .replace(/[^\S\n]{2,}/g, ' ')
        .replace(/[^\S\n]+([,.!?])/g, '$1')
        .trim());
}
export const campagneInputSchema = z.object({
    id: uuidSchema.optional(),
    name: z.string().trim().min(3, 'Au moins 3 caractères').max(120),
    internalSubject: z.string().trim().max(200).optional(),
    message: z
        .string()
        .trim()
        .min(10, 'Le message est trop court pour être utile')
        // 1024 : la limite du corps d'un modèle WhatsApp. La refuser ici évite un
        // rejet de l'opérateur après coup, message par message.
        .max(1024, 'WhatsApp n’accepte pas plus de 1024 caractères'),
    segment: segmentSchema,
    /**
     * Les clients COCHÉS, quand l'utilisateur a fait le tri à la main.
     *
     * ⚠️ Trois valeurs, trois sens différents, à ne jamais confondre :
     *   • absent / null  → tout le segment, sans exception ;
     *   • ["id", …]      → ceux-là seulement ;
     *   • []             → PERSONNE. L'envoi doit alors être refusé, pas
     *                      réinterprété en « tout le monde » — c'est la
     *                      confusion qui envoie 400 messages non voulus.
     */
    selection: z.array(uuidSchema).max(5000).nullish(),
    /**
     * Visuel joint au message : lien public ou image déposée.
     *
     * ⚠️ WhatsApp va CHERCHER l'image à l'adresse fournie. Une image déposée est
     * donc republiée par l'application à une adresse publique ; un `data:` brut
     * ne serait jamais récupérable par l'opérateur.
     */
    mediaUrl: z.string().trim().max(1_500_000).nullish(),
    /** Instant ISO. Absent = la campagne reste au brouillon, lancée à la main. */
    scheduledAt: z.string().datetime({ offset: true }).nullish(),
    options: optionsCampagneSchema.partial().optional(),
});
export const campagneQuerySchema = z.object({
    q: z.string().trim().max(120).optional(),
    statut: z.enum(STATUTS_CAMPAGNE).optional(),
    segment: z.enum(TYPES_SEGMENT).optional(),
    du: jourSchema.optional(),
    au: jourSchema.optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
    offset: z.coerce.number().int().min(0).default(0),
});
/**
 * Contrôle de qualité LOCAL, sans appel réseau.
 *
 * ⭐ Il tourne pendant la frappe et à la préparation. L'assistant IA affine, il
 * ne remplace pas : ces règles-là doivent tenir même sans clé d'API, sinon un
 * message tout en majuscules partirait à 400 personnes le jour où le
 * fournisseur d'IA est en panne.
 */
export function controlerMessage(message) {
    const defauts = [];
    const texte = message.trim();
    if (texte.length < 10) {
        defauts.push({ gravite: 'bloquant', message: 'Le message est trop court.' });
    }
    if (texte.length > 1024) {
        defauts.push({
            gravite: 'bloquant',
            message: `Trop long de ${texte.length - 1024} caractères (limite WhatsApp : 1024).`,
        });
    }
    const lettres = texte.replace(/[^A-Za-zÀ-ÿ]/g, '');
    const majuscules = lettres.replace(/[^A-ZÀ-Þ]/g, '');
    if (lettres.length > 20 && majuscules.length / lettres.length > 0.5) {
        defauts.push({
            gravite: 'avertissement',
            message: 'Trop de majuscules : lu comme un cri, et signalé comme du spam.',
        });
    }
    const liens = texte.match(/https?:\/\/\S+/g) ?? [];
    if (liens.length > 1) {
        defauts.push({
            gravite: 'avertissement',
            message: 'Plusieurs liens : un seul appel à l’action est plus efficace.',
        });
    }
    if (!/[?!.]/.test(texte)) {
        defauts.push({
            gravite: 'conseil',
            message: 'Aucune ponctuation finale — le message paraît coupé.',
        });
    }
    if (!texte.includes('{{prenom}}')) {
        defauts.push({
            gravite: 'conseil',
            message: 'Ajoutez {{prenom}} : un message nommé est lu bien plus souvent.',
        });
    }
    if (!/(stop|désabonn|desabonn)/i.test(texte)) {
        defauts.push({
            gravite: 'conseil',
            message: 'Aucune mention de désinscription — recommandée, et exigée dans plusieurs pays.',
        });
    }
    const emojis = texte.match(/\p{Extended_Pictographic}/gu) ?? [];
    if (emojis.length > 6) {
        defauts.push({
            gravite: 'conseil',
            message: 'Beaucoup d’émojis : l’effet s’inverse au-delà de trois.',
        });
    }
    return defauts;
}
/** Rien de bloquant ne subsiste : la campagne peut partir. */
export function messageEnvoyable(message) {
    return !controlerMessage(message).some((d) => d.gravite === 'bloquant');
}
// -------------------------------------------------------------- assistant IA
export const INTENTIONS_IA = [
    'idees',
    'redaction',
    'ton',
    'optimisation',
    'controle',
    'moment',
    'recommandations',
];
export const LIBELLES_INTENTION_IA = {
    idees: 'Idées de campagne',
    redaction: 'Rédiger le message',
    ton: 'Adapter le ton',
    optimisation: 'Optimiser le contenu',
    controle: 'Vérifier la qualité',
    moment: 'Meilleur moment d’envoi',
    recommandations: 'Recommandations',
};
export const TONS_MESSAGE = ['chaleureux', 'professionnel', 'enjoue', 'sobre', 'urgent'];
export const LIBELLES_TON = {
    chaleureux: 'Chaleureux',
    professionnel: 'Professionnel',
    enjoue: 'Enjoué',
    sobre: 'Sobre',
    urgent: 'Urgent',
};
export const OBJECTIFS_CAMPAGNE = [
    'reactivation',
    'promotion',
    'nouveaute',
    'fidelisation',
    'evenement',
    'rappel',
];
export const LIBELLES_OBJECTIF = {
    reactivation: 'Faire revenir les clientes perdues de vue',
    promotion: 'Annoncer une offre ou une remise',
    nouveaute: 'Présenter un nouveau soin ou produit',
    fidelisation: 'Remercier et fidéliser',
    evenement: 'Inviter à un événement',
    rappel: 'Rappeler un rendez-vous ou une échéance',
};
export const assistantInputSchema = z.object({
    intention: z.enum(INTENTIONS_IA),
    objectif: z.enum(OBJECTIFS_CAMPAGNE).optional(),
    ton: z.enum(TONS_MESSAGE).optional(),
    /** Le message en cours de rédaction, quand il y en a un. */
    message: z.string().trim().max(2000).optional(),
    /** Ce que le gérant précise en clair : « offre -20 % sur les massages ». */
    precisions: z.string().trim().max(600).optional(),
    segment: segmentSchema.optional(),
    campagneId: uuidSchema.optional(),
});
//# sourceMappingURL=campagne.js.map